import { Box, Card, CardContent, Typography } from '@mui/material';
import React from 'react';
import { usePartyRoles } from '../../hooks/usePartyRoles';
import type { RoleLabelsByKey } from '../../object-actions/types/types';
import type { ManageAssignmentParty, ManageAssignmentRoleVisual } from './manageAssignmentRoleMedia';

const PARTY_TO_ROLE_KEY: Record<ManageAssignmentParty, keyof RoleLabelsByKey> = {
    athlete: 'athlete',
    coach: 'coach',
    guardian: 'parent',
};

/** Dark at top for the label; fades to fully transparent at the bottom into card content. */
const VERTICAL_IMAGE_MASK =
    'linear-gradient(to bottom, rgba(0,0,0) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)';

interface ManageAssignmentRoleCardProps {
    visual: ManageAssignmentRoleVisual;
    children: React.ReactNode;
    /** Taller hero band for full-width athlete row */
    headerMinHeight?: number;
}

const ManageAssignmentRoleCard: React.FC<ManageAssignmentRoleCardProps> = ({
    visual,
    children,
    headerMinHeight = 180,
}) => {
    const { getPartyRoleLabel } = usePartyRoles();
    const label = getPartyRoleLabel(PARTY_TO_ROLE_KEY[visual.party]);

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    minHeight: headerMinHeight,
                    backgroundImage: `url(${visual.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: VERTICAL_IMAGE_MASK,
                    }}
                />
                <Typography
                    component="h2"
                    variant="h2"
                    sx={{
                        position: 'relative',
                        px: 2,
                        pt: 1.5,
                        pb: 1.5,
                        color: 'common.white',
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                    }}
                >
                    {label}
                </Typography>
            </Box>
            <CardContent sx={{ flexGrow: 1, pt: 2, '&:last-child': { pb: 2 } }}>{children}</CardContent>
        </Card>
    );
};

export default ManageAssignmentRoleCard;
